import type { FC, ReactNode } from 'react';
import type { CatalogTarget } from 'src/hooks/useCatalogRoute';
import type { CatalogEntry, ApiCatalogFacetModel } from 'src/types/common';
import type { CatalogVertical, CatalogFilterValues } from 'src/utils/catalogUrl';

import { CaretDown } from '@phosphor-icons/react';
import styled, { css, useTheme } from 'styled-components';
import React, { useId, useRef, useState, useEffect, useCallback } from 'react';

import { pluralCars } from 'src/utils/pluralCars';
import {
  toSaleParams,
  toRentalParams,
  parseCatalogSearch,
  toSaleFacetsParams,
  toRentalFacetsParams,
} from 'src/utils/catalogUrl';

import { useListingsPage, useCatalogFacets, useListRentalListings } from 'src/queries';

import { Button } from 'src/components/themed/Button';

import { FUEL_LABELS, GEARBOX_LABELS, BODY_TYPE_LABELS } from 'src/types/car';

/** What the panel shows: the make and model of the view, and its facets. */
export interface CatalogFiltersValues {
  /** The make and the model the address names — its slugs, whether the catalogue has resolved them or not. */
  makeSlug?: string;
  modelSlug?: string;
  /**
   * The same two as the catalogue has them, once it has answered (`useCatalogRoute`). They give a
   * list the name of an entry no car carries under the filters; the choice itself is the slugs.
   */
  make?: CatalogEntry;
  model?: CatalogEntry;
  filters: CatalogFilterValues;
}

interface CatalogFiltersProps {
  vertical: CatalogVertical;
  /** The view in the address — the panel's only source of truth. */
  values: CatalogFiltersValues;
  /** Goes to another view: `useCatalogRoute().navigateTo`. */
  onApply: (next: CatalogTarget) => void;
  onReset: () => void;
  /**
   * `sidebar` — the laptop column: every change is applied at once (a list on change, a number on
   * Enter or when the field is left). `drawer` — the phone sheet: changes gather here and are
   * applied together by "Show N cars".
   */
  mode: 'sidebar' | 'drawer';
  /** The dealer page: the lists and "Show N cars" count this dealer's cars only. */
  sellerId?: string;
}

/** A make or a model in a list, with the number of cars behind it once the api has counted them. */
interface CountedOption extends CatalogEntry {
  count?: number;
}

/**
 * What the lists on screen are to the filters in force: the api's `answer` for these very filters;
 * the previous lists, shown while the answer is `awaited`; or lists `refused` — the last ones the
 * api gave, kept so a make can still be picked, though nothing was counted under these filters.
 */
type ListsState = 'answer' | 'awaited' | 'refused';

/**
 * What a list offers (storefront-catalog spec §4.4): the entries the facets count — only makes and
 * models that have a car under the filters — in the order the api gave them. The chosen entry is
 * always among them: when the facets do not hold it, it has no car under these filters and stands
 * first in its list at zero, so the field still shows what the address says. It is zero only by an
 * answer for these very filters — until then it stands without a number; and lists that were
 * refused keep their names and lose their numbers, which belonged to other filters.
 */
const optionsOf = (
  facets: ApiCatalogFacetModel[] | undefined,
  chosen: CatalogEntry | undefined,
  state: ListsState
): CountedOption[] => {
  const options: CountedOption[] = (facets ?? []).map(({ id, name, slug, count }) => ({
    id,
    name,
    slug,
    count: state === 'refused' ? undefined : count,
  }));

  if (!chosen || options.some((option) => option.slug === chosen.slug)) return options;

  return [{ ...chosen, count: state === 'answer' ? 0 : undefined }, ...options];
};

/** The entry behind a chosen slug, from whoever knows it; a slug nobody has resolved has none. */
const entryFor = (slug: string | undefined, ...known: (CatalogEntry | undefined)[]): CatalogEntry | undefined =>
  slug ? known.find((entry) => entry?.slug === slug) : undefined;

/** "BMW (5)" — and the bare name while the count is not known. */
const optionLabel = ({ name, count }: CountedOption): string => (count === undefined ? name : `${name} (${count})`);

type NumberFacet = 'yearFrom' | 'yearTo' | 'priceFrom' | 'priceTo' | 'dailyFrom' | 'dailyTo';

/** The number fields of each vertical, in pairs: from, to. */
const NUMBER_FIELDS: Record<CatalogVertical, [NumberFacet, string][][]> = {
  buy: [
    [
      ['yearFrom', 'Year from'],
      ['yearTo', 'Year to'],
    ],
    [
      ['priceFrom', 'Price from'],
      ['priceTo', 'Price to'],
    ],
  ],
  rent: [
    [
      ['dailyFrom', 'Price per day from'],
      ['dailyTo', 'Price per day to'],
    ],
  ],
};

type AttributeFacet = 'body' | 'gearbox' | 'fuel';

/**
 * The car's own attributes (decision №37): the same three lists on both verticals, the api's values
 * under the words the dealer picked them by in the cabinet. The api counts makes and models, not
 * these — so a list offers every value, and a value no car carries finds nothing.
 */
const ATTRIBUTE_FIELDS: { name: AttributeFacet; label: string; any: string; options: [string, string][] }[] = [
  { name: 'body', label: 'Body type', any: 'Any body type', options: Object.entries(BODY_TYPE_LABELS) },
  { name: 'gearbox', label: 'Gearbox', any: 'Any gearbox', options: Object.entries(GEARBOX_LABELS) },
  { name: 'fuel', label: 'Fuel', any: 'Any fuel', options: Object.entries(FUEL_LABELS) },
];

/** The edge of the sheet's fields that fades: the one — or both — past which more of them lie. */
const edgeWithMore = ({ above, below }: { above: number; below: number }) =>
  above && below ? 'both' : above ? 'above' : below ? 'below' : undefined;

/** How far each edge fades, handed to the mask of `Fields` as custom properties. */
const fadeOf = ({ above, below }: { above: number; below: number }) =>
  ({ '--fade-above': `${above}px`, '--fade-below': `${below}px` }) as React.CSSProperties;

/** How long "Show N cars" waits for the typing to stop before it asks the api. */
const COUNT_SETTLE_MS = 300;

/**
 * A typed value, read by the same rules as the address: a number of zero or more (a whole one for
 * a year), a VIN of up to 32 characters — anything else is no value at all.
 */
const readTyped = (name: NumberFacet | 'vin' | AttributeFacet, typed: string, vertical: CatalogVertical) =>
  parseCatalogSearch(new URLSearchParams({ [name]: typed }), vertical).filters[name];

const asText = (value: number | string | undefined) => (value === undefined ? '' : String(value));

const entryOf = ({ id, name, slug }: CatalogEntry): CatalogEntry => ({ id, name, slug });

/** The value after it has stopped changing for `delay` — one request for a finished number, not one per digit. */
const useSettled = <T,>(value: T, delay: number): T => {
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState(key);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(key), delay);
    return () => clearTimeout(timer);
  }, [key, delay]);

  return JSON.parse(settled) as T;
};

/**
 * The catalog's filters (storefront-catalog spec §4.3): one panel for both verticals and every
 * catalog screen — a column beside the grid on a laptop, a sheet behind the funnel on a phone.
 *
 * The choice is never its own: the values come from the address, and applying one is going to
 * another address, so Back, Forward and a reload keep what the panel shows. (What it does keep is
 * the last lists the api gave, to stand in while the next ones are refused.)
 * The makes and models it offers are the facets of the feed under the filters in force — the
 * address's in the column, the sheet's own pending choice in the sheet — each with its count. The
 * choice itself is the slugs, as in the address: a make the catalogue has not resolved yet (or does
 * not know) shows as no choice, and "Show" still carries it on rather than dropping it.
 * Under the numbers stand the car's own attributes — body, gearbox, fuel — the same on both verticals.
 * Every field has a visible label (the name a screen reader and speech input use); the fields are
 * native `select` and `input`, set in the text face at the size of the `heading` step and a regular
 * weight — 17px, over the 16px below which iOS Safari zooms into a focused field.
 */
export const CatalogFilters: FC<CatalogFiltersProps> = ({
  vertical,
  values,
  onApply,
  onReset,
  mode,
  sellerId,
}) => {
  const drawer = mode === 'drawer';
  const id = useId();
  // The sheet's fields scroll in a box of their own (`Fields`), and a box that scrolls on a phone shows
  // no scrollbar: where its edge falls between two fields the sheet looks complete, and the fields past
  // the edge are never found. So the box is told how much of its fields lies past each edge — in
  // pixels, up to the reach of the fade — and fades that far: nothing at an end, the more the deeper.
  // The box's own padding (room for a focus ring) is not a field: what counts starts past it.
  const fieldsBox = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState({ above: 0, below: 0 });
  const boxPadding = useTheme().space.xxs;
  const lookForMore = useCallback(() => {
    const box = fieldsBox.current;

    if (!box) return;

    const past = (pixels: number) => Math.min(SHEET_FADE, Math.max(0, Math.round(pixels) - boxPadding));
    const above = past(box.scrollTop);
    const below = past(box.scrollHeight - box.clientHeight - box.scrollTop);

    setHidden((seen) => (seen.above === above && seen.below === below ? seen : { above, below }));
  }, [boxPadding]);

  useEffect(() => {
    const box = fieldsBox.current;

    if (!drawer || !box || typeof ResizeObserver === 'undefined') return undefined;

    // The box changes (a phone turned on its side) and so does what it holds ("More filters"
    // unfolding): either moves the edge. The observer also reports once at the start.
    const observer = new ResizeObserver(lookForMore);

    [box, ...Array.from(box.children)].forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [drawer, lookForMore]);
  // The sheet's own copy of the choice until "Show"; the column shows the address itself.
  const [pending, setPending] = useState<CatalogFiltersValues>(values);
  const shown = drawer ? pending : values;
  const [moreOpen, setMoreOpen] = useState(!!values.filters.vin);
  const scope = sellerId ? { sellerId } : {};

  // The lists: the facets of the feed under the filters shown — asked at once in the column, where a
  // filter is an address already, and once the typing settles in the sheet.
  const facetsWanted = {
    ...(vertical === 'buy' ? toSaleFacetsParams(shown.filters) : toRentalFacetsParams(shown.filters)),
    ...scope,
  };
  const facetsSettled = useSettled(facetsWanted, COUNT_SETTLE_MS);
  const facetsQuery = useCatalogFacets(vertical, drawer ? facetsSettled : facetsWanted);
  // The last lists the api gave: they stay on screen through a refusal of the next ones.
  const [lastLists, setLastLists] = useState(facetsQuery.data);

  if (facetsQuery.data && facetsQuery.data !== lastLists) setLastLists(facetsQuery.data);

  const lists = facetsQuery.data ?? lastLists;
  // A refusal stands until an answer comes. Refused lists are asked for again on their own, and each
  // new attempt puts the query back to waiting with the previous lists in hand; a filter changed
  // meanwhile is a question that has not failed yet. Read as "awaited", either would bring the other
  // filters' numbers back for as long as the attempt lasts — so lists that lost their numbers to a
  // refusal stay without them until the api answers again.
  const answered = facetsQuery.data !== undefined && !facetsQuery.isPlaceholderData;
  const [stripped, setStripped] = useState(false);

  if (answered ? stripped : facetsQuery.isError && !stripped) setStripped(!answered);

  const listsState: ListsState = answered ? 'answer' : facetsQuery.isError || stripped ? 'refused' : 'awaited';

  // The chosen make and model as entries: counted by the facets, picked from a list in the sheet,
  // or resolved for the address — whichever knows the slug. A slug nobody knows (yet) keeps its
  // place in the choice and simply has no entry: no option to show, no id to count by.
  const facetMake = lists?.makes.find((entry) => entry.slug === shown.makeSlug);
  const chosenMake = entryFor(shown.makeSlug, facetMake, shown.make, values.make);
  const chosenModel = chosenMake
    ? entryFor(
        shown.modelSlug,
        facetMake?.models.find((entry) => entry.slug === shown.modelSlug),
        shown.model,
        values.model
      )
    : undefined;
  const makeOptions = optionsOf(lists?.makes, chosenMake, listsState);
  const modelOptions = optionsOf(facetMake?.models, chosenModel, listsState);

  // "Show N cars": how many the sheet's choice finds, asked once the typing settles.
  const choice = { makeId: chosenMake?.id, modelId: chosenModel?.id, filters: pending.filters };
  const saleCount = { ...toSaleParams(choice), limit: 1, ...scope };
  const rentCount = { ...toRentalParams(choice), limit: 1, ...scope };
  const settledSale = useSettled(saleCount, COUNT_SETTLE_MS);
  const settledRent = useSettled(rentCount, COUNT_SETTLE_MS);
  const saleCountQuery = useListingsPage(settledSale, { enabled: drawer && vertical === 'buy' });
  const rentCountQuery = useListRentalListings(settledRent, { enabled: drawer && vertical === 'rent' });
  const [countQuery, counted, wanted] =
    vertical === 'buy'
      ? [saleCountQuery, settledSale, saleCount]
      : [rentCountQuery, settledRent, rentCount];
  const count =
    JSON.stringify(counted) === JSON.stringify(wanted) && !countQuery.isPlaceholderData
      ? countQuery.data?.total
      : undefined;

  // A make or a model picked from its list; the empty value is "Any". The sheet keeps the entry
  // beside the slug: the next lists may no longer hold it, and it still has to be named.
  const pickMake = (slug: string) => {
    if (!drawer) {
      onApply({ makeSlug: slug || undefined });
      return;
    }

    const make = makeOptions.find((option) => option.slug === slug);

    // A new make drops the model, as the address does.
    setPending((current) => ({
      ...current,
      makeSlug: make?.slug,
      make: make && entryOf(make),
      modelSlug: undefined,
      model: undefined,
    }));
  };

  const pickModel = (slug: string) => {
    if (!drawer) {
      onApply({ modelSlug: slug || undefined });
      return;
    }

    const model = modelOptions.find((option) => option.slug === slug);

    setPending((current) => ({ ...current, modelSlug: model?.slug, model: model && entryOf(model) }));
  };

  // One facet at a time: the column applies it on top of the address, the sheet on top of its own
  // latest choice — merged inside the update, so two quick edits never overwrite each other.
  const setFacet = (name: NumberFacet | 'vin' | AttributeFacet, value: number | string | undefined) => {
    if (!drawer) {
      onApply({ filters: { ...values.filters, [name]: value } });
      return;
    }
    setPending((current) => ({ ...current, filters: { ...current.filters, [name]: value } }));
  };

  return (
    <Panel $sheet={drawer}>
      {drawer ? null : <Title>Filters</Title>}
      <Fields
        ref={fieldsBox}
        $sheet={drawer}
        data-more={drawer ? edgeWithMore(hidden) : undefined}
        style={drawer ? fadeOf(hidden) : undefined}
        onScroll={drawer ? lookForMore : undefined}
      >
        <Field>
          <Label htmlFor={`${id}-make`}>Make</Label>
          <Select
            id={`${id}-make`}
            name="make"
            value={shown.makeSlug ?? ''}
            onChange={(event) => pickMake(event.target.value)}
          >
            <option value="">Any make</option>
            {makeOptions.map((entry) => (
              <option key={entry.id} value={entry.slug}>
                {optionLabel(entry)}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor={`${id}-model`}>Model</Label>
          <Select
            id={`${id}-model`}
            name="model"
            value={shown.modelSlug ?? ''}
            disabled={!chosenMake}
            onChange={(event) => pickModel(event.target.value)}
          >
            <option value="">Any model</option>
            {modelOptions.map((entry) => (
              <option key={entry.id} value={entry.slug}>
                {optionLabel(entry)}
              </option>
            ))}
          </Select>
        </Field>
        {NUMBER_FIELDS[vertical].map((pair) => (
          <Pair key={pair[0][0]}>
            {pair.map(([name, label]) => (
              <TypedField
                key={name}
                id={`${id}-${name}`}
                name={name}
                label={label}
                type="number"
                value={shown.filters[name]}
                commitOnEveryChange={drawer}
                read={(typed) => readTyped(name, typed, vertical)}
                onCommit={(value) => setFacet(name, value)}
              />
            ))}
          </Pair>
        ))}
        {ATTRIBUTE_FIELDS.map(({ name, label, any, options }) => (
          <Field key={name}>
            <Label htmlFor={`${id}-${name}`}>{label}</Label>
            <Select
              id={`${id}-${name}`}
              name={name}
              value={shown.filters[name] ?? ''}
              // Through the same rules as the address: only a value the api knows becomes a filter.
              onChange={(event) => setFacet(name, readTyped(name, event.target.value, vertical))}
            >
              <option value="">{any}</option>
              {options.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Select>
          </Field>
        ))}
        {vertical === 'buy' ? (
          <More
            open={moreOpen}
            onToggle={(event) => {
              const fold = event.currentTarget;

              // Unfolded by a tap at the bottom edge of a box that scrolls, the VIN would open out of
              // sight and the tap would look dead: the field the tap revealed is brought into view.
              // Only a tap: an address with a VIN unfolds it as the page loads, and a page that
              // opened scrolled down to its VIN would have lost its heading.
              if (fold.open && !moreOpen) fold.querySelector('input')?.scrollIntoView({ block: 'nearest' });
              setMoreOpen(fold.open);
            }}
          >
            <summary>
              More filters
              <CaretDown aria-hidden size={16} />
            </summary>
            <TypedField
              id={`${id}-vin`}
              name="vin"
              label="VIN"
              type="text"
              value={shown.filters.vin}
              commitOnEveryChange={drawer}
              read={(typed) => readTyped('vin', typed, vertical)}
              onCommit={(value) => setFacet('vin', value)}
            />
          </More>
        ) : null}
      </Fields>
      <Actions>
        <Button title="Reset" onClick={onReset} />
        {drawer ? (
          <Button
            title={count === undefined ? 'Show cars' : `Show ${pluralCars(count)}`}
            variant="accent"
            onClick={() =>
              onApply({ makeSlug: pending.makeSlug, modelSlug: pending.modelSlug, filters: pending.filters })
            }
          />
        ) : null}
      </Actions>
    </Panel>
  );
};

interface TypedFieldProps {
  id: string;
  name: string;
  label: ReactNode;
  type: 'number' | 'text';
  value: number | string | undefined;
  /** The sheet takes every keystroke into its choice; the column applies on Enter or on leaving the field. */
  commitOnEveryChange: boolean;
  read: (typed: string) => number | string | undefined;
  onCommit: (value: number | string | undefined) => void;
}

/**
 * A number or a VIN typed into the panel. What is being typed stays in the field; the column applies
 * it on Enter or when the field is left, so a year is one new address, not four.
 */
const TypedField: FC<TypedFieldProps> = ({ id, name, label, type, value, commitOnEveryChange, read, onCommit }) => {
  const [draft, setDraft] = useState(asText(value));
  const [seen, setSeen] = useState(value);

  // A new value from the address (Back, Reset, a link) replaces what the field shows.
  if (value !== seen) {
    setSeen(value);
    setDraft(asText(value));
  }

  const commit = (typed: string) => {
    const next = read(typed);

    // An empty field takes the facet off. Something the rules turn down (a negative price, a year
    // with a fraction) changes nothing, and does not stay on screen as if it were in force: the
    // field goes back to what the address holds.
    if (next === undefined && typed.trim() !== '') {
      if (!commitOnEveryChange) setDraft(asText(value));
      return;
    }
    if (next !== value) onCommit(next);
  };

  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        name={name}
        type={type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        min={type === 'number' ? 0 : undefined}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          if (commitOnEveryChange) commit(event.target.value);
        }}
        onBlur={() => {
          if (!commitOnEveryChange) commit(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !commitOnEveryChange) commit(draft);
        }}
      />
    </Field>
  );
};

/**
 * What a phone's screen holds around the panel of the sheet, in pixels (`Modal.module.css`): the
 * half of the dialog's header badge that stands above the sheet (a 96px image at `top: -48px`), and
 * the sheet's own padding with the gap under its header (68 + 40, and 20).
 */
const SHEET_BADGE_RISE = 48;
const SHEET_CHROME = 128;

/**
 * How far the fade of an edge of the sheet's fields reaches at most — most of a field, so one is
 * always seen going. In a box too short to spare that (a phone on its side) it is a share of the
 * box, though never so little that it could fall wholly into the gap between two fields. Each edge
 * fades as far as fields are hidden past it (`--fade-above`, `--fade-below`, set by the panel): at an
 * end of the box there is no fade at all. In the mask `black` is not a colour of the page: a mask
 * shows what it paints opaque.
 */
const SHEET_FADE = 40;
const FADE_REACH = 'max(24px, 18%)';
const FIELDS_MASK = [
  'linear-gradient(to bottom',
  `transparent, black min(var(--fade-above), ${FADE_REACH})`,
  `black calc(100% - min(var(--fade-below), ${FADE_REACH})), transparent)`,
].join(', ');

/**
 * The panel. In the column it is as tall as its fields. The sheet is pinned to the bottom of a
 * phone's screen and cannot grow past its top: with the three lists of a car's attributes it is
 * taller than most phones, and whatever went above the screen — the make, first of all — was out of
 * reach. So the sheet's panel is no taller than the screen has room for — the screen less a gap of
 * air, the rise of the dialog's badge (a sheet that climbed any higher would cut it) and the
 * dialog's chrome; `dvh` where the browser has it, so a phone's address bar is not counted as
 * screen — and inside it the fields give way: they scroll, and "Reset" and "Show N cars" stay under
 * them, always on screen, however many lines the buttons take.
 */
const Panel = styled.div<{ $sheet: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md}px;
  width: 100%;
  min-width: 0;

  ${({ $sheet, theme }) =>
    $sheet &&
    css`
      max-height: calc(100vh - ${theme.space.md + SHEET_BADGE_RISE + SHEET_CHROME}px);
      max-height: calc(100dvh - ${theme.space.md + SHEET_BADGE_RISE + SHEET_CHROME}px);
    `}
`;

/**
 * The fields. In the column they simply stack. In the sheet they take what the panel has left and
 * scroll inside it. The edge past which more of them lie fades out, as far as they are hidden (the
 * panel sets it from where the box is scrolled to): a phone shows no scrollbar, and without the
 * fade a cut that falls between two fields looks like the end of the list. A field the keyboard
 * brings into view stops clear of the fade, with its label and its focus ring. The padding is room for the focus
 * ring of a field at the edge of the box, which would otherwise clip it; the negative margin hands
 * that room back.
 *
 * A thumb's drag belongs to the box only while fields lie out of sight (`data-more`). With all of
 * them in view the box has nothing to scroll, and a drag it still allowed went to whatever scrolls
 * behind the sheet: on an iPhone the page under the dialog moved (a tall phone in the Telegram Mini
 * App, where the whole sheet fits; any browser on the shorter sheet of the rentals). The dialog
 * around the box takes no gesture at all (`Modal.module.css`), but that does not reach in here: a
 * box that scrolls by its style opens the gestures anew for itself and for what it holds, whether
 * or not it has anything to scroll. So `none` has to stand on the box itself — it is not a repeat
 * of the dialog's, and taking it off brings the moving page back. The mark is set only past the
 * box's own padding (`lookForMore`), so a box that carries it always has something to scroll.
 */
const Fields = styled.div<{ $sheet: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md}px;
  min-width: 0;

  ${({ $sheet, theme }) =>
    $sheet &&
    css`
      flex: 0 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      touch-action: none;
      padding: ${theme.space.xxs}px;
      margin: -${theme.space.xxs}px;
      scroll-padding: min(${SHEET_FADE + 28}px, 25%) 0 min(${SHEET_FADE + 8}px, 20%);
      --fade-above: 0px;
      --fade-below: 0px;
      -webkit-mask-image: ${FIELDS_MASK};
      mask-image: ${FIELDS_MASK};

      &[data-more] {
        touch-action: pan-y;
      }
    `}
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.typography.title3.fontSize}px;
  line-height: ${({ theme }) => theme.typography.title3.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.white};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xxs}px;
  min-width: 0;
`;

const Pair = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.space.xs}px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.medium.fontSize}px;
  line-height: ${({ theme }) => theme.typography.medium.lineHeight}px;
  color: ${({ theme }) => theme.colors.lightWhite};
`;

/* Fields: the heading step's size (17px — iOS zooms into anything under 16) with a regular weight. */
const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ theme }) => theme.control.height}px;
  padding: 0 ${({ theme }) => theme.space.sm}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small}px;
  background: ${({ theme }) => theme.colors.surfaceRaised};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.heading.fontSize}px;
  line-height: ${({ theme }) => theme.typography.heading.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color-scheme: dark;

  &:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;

const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ theme }) => theme.control.height}px;
  padding: 0 ${({ theme }) => theme.space.sm}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small}px;
  background: ${({ theme }) => theme.colors.surfaceRaised};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.heading.fontSize}px;
  line-height: ${({ theme }) => theme.typography.heading.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color-scheme: dark;

  &:disabled {
    color: ${({ theme }) => theme.colors.mutedWhite};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;

/* "More filters" — the VIN, folded away (spec §4.3). The summary is a thumb tall like any target;
   its caret turns when the fold is open, in place of the browser's marker that `display: flex` drops. */
const More = styled.details`
  & > summary {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.xs}px;
    list-style: none;
    min-height: ${({ theme }) => theme.control.touchTarget}px;
    cursor: pointer;
    font-family: ${({ theme }) => theme.fonts.text};
    font-size: ${({ theme }) => theme.typography.large.fontSize}px;
    line-height: ${({ theme }) => theme.typography.large.lineHeight}px;
    color: ${({ theme }) => theme.colors.lightWhite};
  }

  & > summary::-webkit-details-marker {
    display: none;
  }

  & > summary svg {
    transition: transform 0.2s;
  }

  &[open] > summary svg {
    transform: rotate(180deg);
  }

  & > summary:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.space.xs}px;
`;
